/**
 * Contact edit section for the venue detail page Details tab. Reads as a
 * record by default (ReadGrid) and edits in place via EditSection, matching
 * the other sections on the tab.
 */
import type { VenueDetail, VenuePatch } from '../../../../api/venues';
import { Field } from '../venueCreateFields';

import { EditSection } from './EditSection';
import { Input } from './Input';
import { ReadGrid } from './ReadGrid';
import { useFormDraft } from './use-form-draft';

const nullable = (s: string) => (s.trim().length === 0 ? null : s.trim());

interface SectionProps {
  venue: VenueDetail;
  readOnly: boolean;
  save: (patch: VenuePatch) => Promise<void>;
}

export function ContactSection({ venue, readOnly, save }: SectionProps) {
  const seed = {
    contactName: venue.contactName ?? '',
    contactPhone: venue.contactPhone ?? '',
    contactEmail: venue.contactEmail ?? '',
  };
  const { draft, setField, reset } = useFormDraft(seed, venue.updatedAt);

  return (
    <EditSection
      title="Contact"
      hint="venue-side"
      readOnly={readOnly}
      onCancel={reset}
      onSave={() =>
        save({
          contactName: nullable(draft.contactName),
          contactPhone: nullable(draft.contactPhone),
          contactEmail: nullable(draft.contactEmail),
        })
      }
      view={
        <ReadGrid
          rows={[
            { label: 'Name', value: venue.contactName },
            { label: 'Phone', value: venue.contactPhone },
            { label: 'Email', value: venue.contactEmail },
          ]}
        />
      }
      form={
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="Name">
            <Input value={draft.contactName} onChange={(v) => setField('contactName', v)} />
          </Field>
          <Field label="Phone">
            <Input
              type="tel"
              value={draft.contactPhone}
              onChange={(v) => setField('contactPhone', v)}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={draft.contactEmail}
              onChange={(v) => setField('contactEmail', v)}
            />
          </Field>
        </div>
      }
    />
  );
}
